import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container, TextField, Typography, Button, Box, Paper, Grid, IconButton, Card, CardContent, CardHeader, Divider, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Accordion, AccordionSummary, AccordionDetails, TablePagination, Input, FormControl, InputLabel, InputAdornment, Modal, Dialog, DialogTitle, DialogContent, DialogActions, Chip, List, ListItem, ListItemText, Tooltip, CardActions,
    Stack, Avatar, AvatarGroup, Alert, LinearProgress,
    Drawer,
    Tabs,
    Tab,
    Badge,
    Skeleton,
    CircularProgress,
    Switch,
    FormControlLabel
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/Upload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import Layout from '../Layout';
import { styled } from '@mui/material/styles';
import api from '../api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/userContext';
import {
    TRACKER_LOCK_UI_MESSAGE,
    isLockedTrackerStatus,
} from '../utils/trackerStatusLock';
import { canonicalRisk, normalizeSubtaskStatusKey } from '../utils/trackerNormalization';

const subtaskStatusLabel = (status) => {
    const key = normalizeSubtaskStatusKey(status);
    return ({ pending: 'Pending', 'in-progress': 'In-Progress', complied: 'Complied' })[key] || String(status || '');
};

const StyledTableHead = styled(TableHead)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    '& .MuiTableCell-head': {
        color: theme.palette.common.white,
        fontWeight: 'bold'
    }
}));

async function fetchFile(trackerId, setPrefilledFile, setIsSubmitDisabled) {
    try {
        const response = await api.post(
            's3/get',
            { trackerId },
            { responseType: 'blob' }
        );
        const cd = response.headers['content-disposition'];
        const fileName = cd && cd.match(/filename="(.+)"/)?.[1] || 'download';
        const fileBlob = new Blob([response.data], { type: response.headers['content-type'] });
        const fileUrl = URL.createObjectURL(fileBlob);
        setPrefilledFile({ filePath: fileUrl, name: fileName });
        setIsSubmitDisabled(true);
    } catch (err) {
        console.error('Error fetching file:', err);
    }
}

function showApiErrorToast(error, {
    fallback = 'Something went wrong. Please try again.',
    onSessionExpired, // callback
} = {}) {
    // Axios-style error
    const res = error?.response;


    if (!res) {
        toast.error('Something went wrong');
        return;
    }

    const { status, data } = res;
    const serverMsg = data?.message || data?.error || '';

    // 401 – Session expired
    if (status === 401) {
        toast.error('Your session has expired. Please log in again.');
        if (onSessionExpired) onSessionExpired();
        return;
    }

    // 403 – Forbidden
    if (status === 403) {
        toast.error(serverMsg || 'You don’t have permission to perform this action.');
        return;
    }

    // 404 – Not found
    if (status === 404) {
        toast.error(serverMsg || 'The requested item was not found. It may have been removed.');
        return;
    }

    // 400 – Validation / bad request
    if (status === 400) {
        toast.error(serverMsg || 'Some of the data looks invalid. Please check and try again.');
        return;
    }

    // 409 – Conflict (if you start using it)
    if (status === 409) {
        toast.error(serverMsg || 'This record was modified elsewhere. Please refresh and try again.');
        return;
    }

    // 5xx – Server-side
    if (status >= 500) {
        toast.error(serverMsg || 'Server error. Please try again in a moment.');
        return;
    }

    // Default
    toast.error(serverMsg || fallback);
}


const EditPage = () => {
    const { uin } = useParams();
    const navigate = useNavigate();
    const [{ user }] = useAuth();

    // --- role helpers ---
    const rawRole = (user?.role || '').toString().trim().toLowerCase();

    const roleKey = rawRole.replace(/[\s\-_]+/g, '');
    const isSeniorManager = roleKey === 'seniormanager';
    const isAdmin = roleKey === 'admin';

    const role = rawRole;
    const currentUserId = user?._id;
    const [sessionModalOpen, setSessionModalOpen] = useState(false);
    const [data, setData] = useState(null);
    const [formState, setFormState] = useState({
        id: '',
        complianceActivity: '',
        description: '',
        category: '',
        actOrRule: '',
        lawArea: '',
        sectionDesc: '',
        sourceURL: '',
        periodicity: '',
        form: '',
        consequence: '',
        risk: '',
        dueDate: '',
        completionDate: '',
        status: '',
        proofNotRequired: false
    });
    const [uploadedFile, setUploadedFile] = useState(null);
    const [prefilledFile, setPrefilledFile] = useState(null);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
    const [docList, setDocList] = useState([]);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [owners, setOwners] = useState([]);
    const [managers, setManagers] = useState([]);
    const [editFields, setEditFields] = useState({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [comments, setComments] = useState([]);
    const [openCommentModal, setOpenCommentModal] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [userId, setUserId] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [companyId, setCompanyId] = useState('');
    const [subTasks, setSubTasks] = useState([]);
    const [openSubTaskModal, setOpenSubTaskModal] = useState(false);
    const [newSubTask, setNewSubTask] = useState({
        complianceActivity: '',
        description: '',
        owners: [],
        managers: [],
        risk: '',
        consequence: '',
        status: 'Pending',
        completionDate: ''
    });
    const [proofFile, setProofFile] = useState(null);
    const [commentText, setCommentText] = useState('');
    const [editingSubTask, setEditingSubTask] = useState(null);
    const [proofTarget, setProofTarget] = useState(null);
    const [openPanel, setOpenPanel] = useState(null);
    const [subtaskQuery, setSubtaskQuery] = useState('');
    const deferredQuery = React.useDeferredValue(subtaskQuery);
    const [subtaskRiskFilter, setSubtaskRiskFilter] = useState([]);
    const [subtaskStatusFilter, setSubtaskStatusFilter] = useState([]);
    const [subtaskLoadingId, setSubtaskLoadingId] = useState(null);
    const [subtaskSort, setSubtaskSort] = useState({ by: 'complianceActivity', dir: 'asc' });
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailTab, setDetailTab] = useState(0);
    const [selectedSubTask, setSelectedSubTask] = useState(null);
    const [subtaskloading, setsubtaskLoading] = useState(false);
    const [fileBusy, setFileBusy] = useState(false);
    const [docDeletingId, setDocDeletingId] = useState(null);
    const [proofBusy, setProofBusy] = useState(false);
    const [pageBusy, setPageBusy] = useState(true);
    const [commentBusy, setCommentBusy] = useState(false);
    const [detailTransitionPending, startDetailTransition] = React.useTransition();
    const [filtersTransitionPending, startFiltersTransition] = React.useTransition();
    const trackerLockedForNonAdmin = !isAdmin && isLockedTrackerStatus(data?.status);
    const canMutateTracker = !trackerLockedForNonAdmin;
    const canManageProofDocs = !isSeniorManager && canMutateTracker;
    // Tracker reassignment: only Admin, Senior Manager, Manager can reassign/remove
    const canReassign = (isAdmin || isSeniorManager || role === 'manager') && canMutateTracker;
    const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
    const [reassignRole, setReassignRole] = useState(null); // 'Owner' | 'Manager'
    const [reassignSelected, setReassignSelected] = useState([]);
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [removeRole, setRemoveRole] = useState(null);
    const [removeTarget, setRemoveTarget] = useState(null); // { email, name? }
    const [assignmentBusy, setAssignmentBusy] = useState(false);
    const notifyLockedAction = React.useCallback(() => {
        if (!trackerLockedForNonAdmin) return false;
        toast.info(TRACKER_LOCK_UI_MESSAGE);
        return true;
    }, [trackerLockedForNonAdmin]);

    useEffect(() => {
        if (!selectedSubTask) return;
        const fresh = subTasks.find(s => s._id === selectedSubTask._id);
        if (fresh) setSelectedSubTask(fresh);
    }, [subTasks, selectedSubTask?._id]);

    const openDetail = (t) => {
        setSelectedSubTask(t);
        setDetailOpen(true);
        setDetailTab(0);
        if (!t.signedDocs) loadProofList(t._id);
    };

    const closeDetail = () => {
        setDetailOpen(false);
        setSelectedSubTask(null);
        setCommentText('');
    };

    const statusMeta = {
        'Pending': { color: 'warning', icon: <PendingActionsIcon sx={{ fontSize: 16 }} /> },
        'In-Progress': { color: 'info', icon: <MoreHorizIcon sx={{ fontSize: 16 }} /> },
        'Complied': { color: 'success', icon: <CheckCircleRoundedIcon sx={{ fontSize: 16 }} /> },
    };

    const StatusChipUI = ({ value }) => {
        const label = subtaskStatusLabel(value);
        const meta = statusMeta[label] || { color: 'default' };
        return (
            <Chip
                size="small"
                variant="soft"
                color={meta.color}
                icon={meta.icon || null}
                label={label ? label.replace('-', ' ') : '—'}
                sx={{ fontWeight: 600 }}
            />
        );
    };

    const RiskChipUI = ({ value }) => {
        const map = { High: 'error', Medium: 'warning', Low: 'success' };
        return (
            <Chip
                size="small"
                color={map[value] || 'default'}
                variant="outlined"
                label={value || '—'}
                sx={{ fontWeight: 600 }}
            />
        );
    };

    const EmailAvatars = ({ csv = '' }) => {
        const emails = csv.split(',').map(s => s.trim()).filter(Boolean);
        if (!emails.length) return <Typography variant="caption" color="text.secondary">—</Typography>;
        return (
            <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 26, height: 26, fontSize: 12 } }}>
                {emails.map(e => (
                    <Tooltip title={e} key={e}>
                        <Avatar>{e[0]?.toUpperCase()}</Avatar>
                    </Tooltip>
                ))}
            </AvatarGroup>
        );
    };

    const riskColor = (theme, risk) => {
        const normalized = canonicalRisk(risk);
        if (normalized === 'High') return theme.palette.error.main;
        if (normalized === 'Medium') return theme.palette.warning.main;
        if (normalized === 'Low') return theme.palette.success.main;
        return theme.palette.grey[500];
    };

    const fetchDocList = async (trackerId) => {
        const { data } = await api.get('s3/list', { params: { trackerId } });
        setDocList(data.active || []);
    };

    const NON_EDITABLE_FIELDS = new Set(['id']);
    const OWNER_EDITABLE = new Set(['completionDate', 'status']);
    const MANAGER_EDITABLE = new Set(['completionDate', 'status']);

    const canEditField = React.useCallback((field) => {
        if (NON_EDITABLE_FIELDS.has(field)) return false;
        if (trackerLockedForNonAdmin) return false;

        const r = role; // already lowercased

        if (r === 'admin') {
            // admin can edit anything except NON_EDITABLE
            return true;
        }

        if (r === 'manager') {
            return MANAGER_EDITABLE.has(field);
        }

        if (r === 'owner') {
            return OWNER_EDITABLE.has(field);
        }

        // senior_manager + any other roles are view-only
        return false;
    }, [role, trackerLockedForNonAdmin]);


    useEffect(() => {
        let alive = true;
        const abort = new AbortController();
        (async () => {
            try {
                setPageBusy(true);
                const { data: uinfo } = await api.get('detail/userinfo?view=auth', { signal: abort.signal });
                if (!alive) return;
                setUserId(uinfo._id);
                setUserEmail(uinfo.email);
                const compId = uinfo.companyDetails?._id;
                setCompanyId(compId || '');
                const [people, trackerRes] = await Promise.all([
                    compId ? api.post('company-detail/companyinfo', { id: compId }, { signal: abort.signal }) : Promise.resolve({ data: { json: { managers: [], owners: [] } } }),
                    api.get(`tracker/get_tracker/${uin}`, { withCredentials: true, signal: abort.signal })
                ]);
                if (!alive) return;
                const tracker = Array.isArray(trackerRes.data) ? trackerRes.data[0] : trackerRes.data;
                if (!tracker) {
                    toast.error('No such Tracker');
                    throw new Error('Tracker not found');
                }
                const assignedTo = tracker.assignedTo || [];
                const trackerOwner = tracker.trackerOwner || [];
                const trackerManager = tracker.trackerManager || [];
                const isAssigned = assignedTo.includes(userEmail);
                // Backend may return populated { _id, email, name } or raw id; normalize for comparison
                const toUserId = (x) => (x && typeof x === 'object' && x._id != null ? x._id : x);
                const isOwner = trackerOwner.some((o) => String(toUserId(o)) === String(user._id));
                const isManager = trackerManager.some((m) => String(toUserId(m)) === String(user._id));

                const isAdmin = role === 'admin';
                const isSeniorManager = role === 'senior_manager' || role === 'seniormanager';

                if (!isAdmin && !isSeniorManager && !isAssigned && !isOwner && !isManager) {
                    navigate('/dashboard');
                    return;
                }

                startDetailTransition(() => {
                    setData(tracker);
                    setManagers(people.data.json.managers || []);
                    setOwners(people.data.json.owners || []);
                    setComments(tracker.comment || []);
                    setFormState({
                        id: tracker._id,
                        complianceActivity: tracker.complianceActivity,
                        description: tracker.description,
                        category: tracker.category,
                        actOrRule: tracker.actOrRule,
                        lawArea: tracker.lawArea,
                        sectionDesc: tracker.sectionDesc,
                        sourceURL: tracker.sourceURL,
                        periodicity: tracker.periodicity,
                        consequence: tracker.consequence,
                        risk: tracker.risk,
                        dueDate: tracker.dueDate?.slice(0, 10) || '',
                        completionDate: tracker.completionDate?.slice(0, 10) || '',
                        status: tracker.status,
                        proofNotRequired: Boolean(tracker.proofNotRequired),
                    });
                    setSubTasks(tracker.subTasks || []);
                });
                fetchDocList(tracker._id);
                fetchFile(tracker._id, setPrefilledFile, setIsSubmitDisabled);
            } catch (err) {
                if (err?.name !== 'CanceledError') {
                    console.error(err);
                    showApiErrorToast(err, {
                        fallback: 'We could not load this tracker. Please refresh the page.',
                        onSessionExpired: () => setSessionModalOpen(true),
                    });
                }
            }
            finally {
                if (alive) setPageBusy(false);
            }
        })();
        return () => { alive = false; abort.abort(); };
    }, [uin, role, navigate]);

    const refreshTracker = React.useCallback(async () => {
        try {
            const { data } = await api.get(`tracker/get_tracker/${uin}`);
            if (!Array.isArray(data) || !data.length) return;
            const t = data[0];
            setData(t);
            setFormState({
                id: t._id,
                complianceActivity: t.complianceActivity,
                description: t.description,
                category: t.category,
                actOrRule: t.actOrRule,
                lawArea: t.lawArea,
                sectionDesc: t.sectionDesc,
                sourceURL: t.sourceURL,
                periodicity: t.periodicity,
                consequence: t.consequence,
                risk: t.risk,
                dueDate: t.dueDate?.slice(0, 10) || '',
                completionDate: t.completionDate?.slice(0, 10) || '',
                status: t.status,
                proofNotRequired: Boolean(t.proofNotRequired),
            });
            setComments(t.comment || []);
            setSubTasks(t.subTasks || []);
            fetchDocList(t._id);
            fetchFile(t._id, setPrefilledFile, setIsSubmitDisabled);
        } catch (err) {
            console.error('refreshTracker:', err);
            showApiErrorToast(err, {
                fallback: 'Failed to refresh the tracker. Please try again.',
                onSessionExpired: () => setSessionModalOpen(true),
            });
        }

    }, [uin]);

    const handleEditClick = React.useCallback((field) => {
        if (notifyLockedAction()) return;
        setEditFields((prev) => ({ ...prev, [field]: !prev[field] }));
    }, [notifyLockedAction]);

    const handleSessionModalClose = () => {
        setSessionModalOpen(false);
        navigate('/login');
    };

    const handleSubmitFile = async () => {
        if (notifyLockedAction()) return;
        if (!uploadedFile) return;
        setFileBusy(true);
        const form = new FormData();
        form.append('file', uploadedFile);
        form.append('trackerId', formState.id);
        const endpoint = prefilledFile ? 's3/update' : 's3/upload';
        try {
            const { data } = await api.post(endpoint, form, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success(data.message);
            await Promise.all([
                fetchFile(formState.id, setPrefilledFile, setIsSubmitDisabled),
                fetchDocList(formState.id)
            ]);
            setUploadedFile(null);
        } catch (err) {
            console.error(err);
            showApiErrorToast(err, {
                fallback: 'Could not upload the file. Please check the file and try again.',
                onSessionExpired: () => setSessionModalOpen(true),
            });
        }
        finally {
            setFileBusy(false);
        }
    };

    const handleDeleteFile = async (doc) => {
        if (notifyLockedAction()) return;
        setDocDeletingId(doc.filePath);
        try {
            await api.post('s3/delete-file', { trackerId: formState.id, filePath: doc.filePath });
            toast.info(`Deleted ${doc.fileName}`);
            await fetchDocList(formState.id);
        } catch (err) {
            console.error(err);
            showApiErrorToast(err, {
                fallback: 'Could not delete this file. Please try again.',
                onSessionExpired: () => setSessionModalOpen(true),
            });
        } finally {
            setDocDeletingId(null);
        }
    };

    const openProofUpload = (subTaskId) => {
        if (notifyLockedAction() || proofUploadDisabled) return;
        setProofTarget(subTaskId);
        const el = document.getElementById('proof-input');
        if (el) el.click();
    };

    const handleProofFileChange = async (e) => {
        if (!e.target.files?.length || proofUploadDisabled || notifyLockedAction()) return;
        const file = e.target.files[0];
        const form = new FormData();
        form.append('file', file);
        form.append('trackerId', formState.id);
        form.append('subTaskId', proofTarget);
        setProofBusy(true);
        try {
            await api.post('sub-tracker/subtask-proof', form, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Proof uploaded');
            setSubTasks((prev) =>
                prev.map((s) =>
                    s._id === proofTarget
                        ? {
                            ...s,
                            proof: true,
                            supportingDocuments: [
                                ...(s.supportingDocuments || []),
                                { fileName: file.name, filePath: 'just_uploaded', uploadedAt: new Date() }
                            ]
                        }
                        : s
                )
            );
        } catch (err) {
            toast.error('Upload failed');
            console.error(err);
        } finally {
            setProofBusy(false);
            e.target.value = '';
        }
    };

    const openEdit = async (subTask) => {
        if (notifyLockedAction()) return;
        const { data } = await api.get(
            `sub-tracker/subtask-proof-list/${formState.id}/${subTask._id}`
        );
        const enriched = {
            ...subTask,
            signedDocs: data.documents,
            comments: data.comments
        };
        setEditingSubTask(enriched);
        setNewSubTask({
            complianceActivity: subTask.complianceActivity,
            description: subTask.description,
            owners: (subTask.owners || '').split(','),
            managers: (subTask.managers || '').split(','),
            risk: canonicalRisk(subTask.risk),
            consequence: subTask.consequence,
            status: subtaskStatusLabel(subTask.status),
            completionDate: subTask.completionDate?.slice(0, 10) || ''
        });
        setOpenSubTaskModal(true);
    };

    const handleAddComment = async (subTaskId) => {
        if (notifyLockedAction()) return;
        if (!commentText.trim()) return;
        setCommentBusy(true);
        try {
            const { data } = await api.post('sub-tracker/subtask-comment', {
                trackerId: formState.id,
                subTaskId,
                text: commentText
            });
            startDetailTransition(() => setSubTasks(data.subTasks));
            setCommentText('');
            toast.success('Comment added');
        } catch (err) {
            console.error(err);
            showApiErrorToast(err, {
                fallback: 'Could not add your comment. Please try again.',
                onSessionExpired: () => setSessionModalOpen(true),
            });
        } finally {
            setCommentBusy(false);
        }

    };

    const handleSubmitComment = async () => {
        if (notifyLockedAction()) return;
        if (!newComment.trim()) return;
        setCommentBusy(true);
        try {
            const { data } = await api.post('tracker/add-comment', {
                trackerId: uin,
                text: newComment
            });
            if (data?.comment) setComments((prev) => [...prev, data.comment]);
            toast.success('Comment added');
            setNewComment('');
            setOpenCommentModal(false);
        } catch (err) {
            toast.error('Error adding comment');
            console.error(err);
        } finally {
            setCommentBusy(false);
        }
    };

    const handleDeleteComment = async (comment) => {
        if (notifyLockedAction()) return;
        try {
            await api.post('tracker/delete-comment', {
                time: comment?.created,
                _id: formState.id,
                comment
            });
            setComments((prev) => prev.filter((c) => c._id !== comment._id));
            toast.success('Comment removed');
        } catch (err) {
            console.error(err);
            showApiErrorToast(err, {
                fallback: 'Could not remove the comment. Please try again.',
                onSessionExpired: () => setSessionModalOpen(true),
            });
        }

    };

    const handleSubmitSubTask = async () => {
        if (notifyLockedAction()) return;
        const { complianceActivity, description, owners, managers, risk, consequence, status, completionDate } = newSubTask;
        if (!complianceActivity || !description || !owners.length || !managers.length || !risk || !consequence) {
            toast.error('Please fill in all fields for the Sub-Task.');
            return;
        }
        const endpoint = editingSubTask ? 'sub-tracker/subtask-update' : 'sub-tracker/subtask-add';
        const payload = {
            trackerId: formState.id,
            complianceActivity,
            owners,
            managers,
            risk,
            consequence,
            status,
            completionDate
        };
        if (!editingSubTask) payload.description = description;
        if (editingSubTask) payload.subTaskId = editingSubTask._id;
        try {
            setsubtaskLoading(true);
            const { data } = await api.post(endpoint, payload);
            setSubTasks(data.subTasks);
            toast.success(editingSubTask ? 'Sub-Task updated' : 'Sub-Task added');
        } catch (err) {
            console.error(err);
            showApiErrorToast(err, {
                fallback: 'Could not save the sub-task. Please check the details and try again.',
                onSessionExpired: () => setSessionModalOpen(true),
            });
        }
        finally {
            setsubtaskLoading(false);
            setEditingSubTask(null);
            setOpenSubTaskModal(false);
            setNewSubTask({
                complianceActivity: '',
                description: '',
                owners: [],
                managers: [],
                risk: '',
                consequence: '',
                status: 'Pending',
                completionDate: ''
            });
        }
    };

    const handleDeleteSubTask = async (subTaskId) => {
        if (notifyLockedAction()) return;
        try {
            const { data } = await api.post('sub-tracker/subtask-delete', {
                trackerId: formState.id,
                subTaskId
            });
            setSubTasks((prev) => prev.filter((t) => t._id !== subTaskId));
            toast.success('Sub-Task deleted');
        } catch (err) {
            toast.error('Error deleting sub-task');
            console.error(err);
        }
    };

    const handleChangePage = (_, p) => setPage(p);
    const handleChangeRowsPerPage = (e) => { setRowsPerPage(+e.target.value); setPage(0); };
    const handleChangeField = React.useCallback((e) =>
        setFormState((st) => ({ ...st, [e.target.name]: e.target.value })), []);

    const handlePreviewClose = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setPreviewModalOpen(false);
    };

    const handleSave = async (field) => {
        if (notifyLockedAction()) return;
        const value = formState[field];
        const normStatus = (v) => String(v || '').trim().toLowerCase();
        if (field === 'status' && normStatus(value) === 'complied' && !data?.proofNotRequired && docList.length === 0) {
            toast.error('Cannot set status to Complied without proof. Upload at least one document or mark this tracker as Proof not required.');
            return;
        }
        try {
            await api.post(`tracker/update_tracker/${uin}`, {
                [field]: value
            });
            toast.success('Updated');
            setEditFields((prev) => ({ ...prev, [field]: false }));
            await refreshTracker();
        } catch (err) {
            console.error(err);
            showApiErrorToast(err, {
                fallback: 'We could not save this field. Please try again.',
                onSessionExpired: () => setSessionModalOpen(true),
            });
        }
    };

    const proofUploadDisabled = Boolean(data?.proofNotRequired);
    const handleProofNotRequiredChange = async (event) => {
        if (notifyLockedAction()) return;
        const checked = event.target.checked;
        if (checked && docList.length > 0) {
            toast.error('Delete all proof documents before marking as Proof not required.');
            return;
        }
        try {
            await api.post(`tracker/update_tracker/${uin}`, { proofNotRequired: checked });
            toast.success(checked ? 'Tracker marked as Proof not required. Status set to Pending for review.' : 'Proof not required cleared.');
            setFormState((prev) => ({ ...prev, proofNotRequired: checked }));
            setData((prev) => prev ? { ...prev, proofNotRequired: checked, status: checked ? 'pending for review' : prev.status } : null);
            refreshTracker();
        } catch (err) {
            console.error(err);
            showApiErrorToast(err, {
                fallback: 'Could not update Proof not required.',
                onSessionExpired: () => setSessionModalOpen(true),
            });
        }
    };

    const handleOpenPreview = React.useCallback(async (doc) => {
        setSelectedDoc(doc);
        try {
            const { data, headers } = await api.post(
                's3/get',
                { trackerId: formState.id, filePath: doc.filePath },
                { responseType: 'blob' }
            );
            const url = URL.createObjectURL(
                new Blob([data], { type: headers['content-type'] || 'application/pdf' })
            );
            setPreviewUrl(url);
            setPreviewModalOpen(true);
        } catch (err) {
            console.error(err);
            showApiErrorToast(err, {
                fallback: 'We could not open the preview. Try downloading the file instead.',
                onSessionExpired: () => setSessionModalOpen(true),
            });
        }

    }, [formState.id]);

    const handleProofUpload = async () => {
        if (notifyLockedAction()) return;
        if (!proofFile || !editingSubTask) return;
        setProofBusy(true);
        const form = new FormData();
        form.append('file', proofFile);
        form.append('trackerId', formState.id);
        form.append('subTaskId', editingSubTask._id);
        try {
            await api.post('sub-tracker/subtask-proof', form, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Proof uploaded');
            const { data } = await api.get(`tracker/get_tracker/${uin}`);
            startDetailTransition(() => setSubTasks(data[0].subTasks));
            setProofFile(null);
        } catch (e) {
            toast.error('Upload failed');
        } finally {
            setProofBusy(false);
        }
    };

    const loadProofList = async (subTaskId) => {
        try {
            setSubtaskLoadingId(subTaskId);
            const { data } = await api.get(
                `sub-tracker/subtask-proof-list/${formState.id}/${subTaskId}`
            );
            setSubTasks((prev) =>
                prev.map((st) =>
                    st._id === subTaskId
                        ? { ...st, signedDocs: data.documents, comments: data.comments }
                        : st
                )
            );
        } catch (err) {
            toast.error('Could not load proofs');
            console.error(err);
        } finally { setSubtaskLoadingId(null); }
    };

    const STATUS_OPTIONS = [
        { value: 'open', label: 'Open' },
        { value: 'pending for review', label: 'Pending Review' },
        { value: 'pending for discrepancy', label: 'Pending — Discrepancy' },
        { value: 'delayed', label: 'Delayed' },
        { value: 'complied', label: 'Complied' },
        { value: 'not applicable', label: 'N/A' },
    ];

    const STATUS_COLORS = {
        open: 'text.secondary',
        'pending for review': 'warning.main',
        'pending for discrepancy': 'warning.main',
        delayed: 'error.main',
        na: 'text.disabled',
        complied: 'success.main',
    };

    const filteredSubTasks = React.useMemo(() => {
        const q = deferredQuery.trim().toLowerCase();
        const riskOk = (r) => !subtaskRiskFilter.length || subtaskRiskFilter.includes(canonicalRisk(r));
        const statusOk = (s) => !subtaskStatusFilter.length || subtaskStatusFilter.includes(subtaskStatusLabel(s));
        const pool = subTasks.filter(t => {
            const text = `${t.complianceActivity} ${t.description}`.toLowerCase();
            return (!q || text.includes(q)) && riskOk(t.risk) && statusOk(t.status);
        });
        const riskRank = { High: 0, Medium: 1, Low: 2 };
        const statusRank = { 'Pending': 0, 'In-Progress': 1, 'Complied': 2 };
        const cmp = (a, b) => {
            let v = 0;
            if (subtaskSort.by === 'risk') v = (riskRank[canonicalRisk(a.risk)] ?? 99) - (riskRank[canonicalRisk(b.risk)] ?? 99);
            else if (subtaskSort.by === 'status') v = (statusRank[subtaskStatusLabel(a.status)] ?? 99) - (statusRank[subtaskStatusLabel(b.status)] ?? 99);
            else v = String(a.complianceActivity).localeCompare(String(b.complianceActivity));
            return subtaskSort.dir === 'asc' ? v : -v;
        };
        return pool.slice().sort(cmp);
    }, [subTasks, deferredQuery, subtaskRiskFilter, subtaskStatusFilter, subtaskSort]);

    const dateTimeFmt = React.useMemo(() => new Intl.DateTimeFormat(undefined, {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
    }), []);

    const dateFmt = React.useMemo(() => new Intl.DateTimeFormat(undefined, {
        year: 'numeric', month: 'short', day: '2-digit'
    }), []);

    // Normalize people mapped from tracker (backend may return populated { email, name } or IDs)
    const toPerson = (x) => {
        if (x == null) return null;
        if (typeof x === 'object' && x !== null && 'email' in x) return { email: x.email, name: x.name || x.email };
        return { email: String(x), name: null };
    };
    const ownerList = React.useMemo(() =>
        (data?.trackerOwner || []).map(toPerson).filter(Boolean),
        [data?.trackerOwner]);
    const managerList = React.useMemo(() =>
        (data?.trackerManager || []).map(toPerson).filter(Boolean),
        [data?.trackerManager]);
    // Company owners can only be assigned as tracker Owner; company managers only as tracker Manager.
    const assignableOwners = React.useMemo(() =>
        (owners || []).filter((u) => u?.email).map((u) => ({ email: u.email, name: u.name || u.email })),
        [owners]);
    const assignableManagers = React.useMemo(() =>
        (managers || []).filter((u) => u?.email).map((u) => ({ email: u.email, name: u.name || u.email })),
        [managers]);
    const assignableUsersForRole = reassignRole === 'Owner' ? assignableOwners : assignableManagers;
    const assignableUsers = assignableUsersForRole;

    const handleReassignOpen = (roleKey) => {
        if (notifyLockedAction()) return;
        setReassignRole(roleKey);
        const current = roleKey === 'Owner' ? ownerList : managerList;
        setReassignSelected(current.map((p) => p.email));
        setReassignDialogOpen(true);
    };
    const handleReassignClose = () => {
        setReassignDialogOpen(false);
        setReassignRole(null);
        setReassignSelected([]);
    };
    const handleReassignSubmit = async () => {
        if (notifyLockedAction()) return;
        if (!reassignRole || !uin) return;
        setAssignmentBusy(true);
        try {
            await api.post('tracker/assignment-trackers', {
                Action: 'Reassign',
                Role: reassignRole,
                User: reassignSelected,
                UIN: [uin],
            });
            toast.success('Tracker assignment updated. Affected users have been notified.');
            handleReassignClose();
            await refreshTracker();
        } catch (err) {
            console.error(err);
            showApiErrorToast(err, {
                fallback: 'Could not update tracker assignment. Please try again.',
                onSessionExpired: () => setSessionModalOpen(true),
            });
        } finally {
            setAssignmentBusy(false);
        }
    };

    const handleRemoveClick = (roleKey, person) => {
        if (notifyLockedAction()) return;
        setRemoveRole(roleKey);
        setRemoveTarget(person);
        setRemoveDialogOpen(true);
    };
    const handleRemoveClose = () => {
        setRemoveDialogOpen(false);
        setRemoveRole(null);
        setRemoveTarget(null);
    };
    const handleRemoveConfirm = async () => {
        if (notifyLockedAction()) return;
        if (!removeRole || !removeTarget?.email || !uin) return;
        setAssignmentBusy(true);
        try {
            await api.post('tracker/assignment-trackers', {
                Action: 'Unassign',
                Role: removeRole,
                User: [removeTarget.email],
                UIN: [uin],
            });
            toast.success('Person removed from tracker. They have been notified.');
            handleRemoveClose();
            await refreshTracker();
        } catch (err) {
            console.error(err);
            showApiErrorToast(err, {
                fallback: 'Could not remove person from tracker. Please try again.',
                onSessionExpired: () => setSessionModalOpen(true),
            });
        } finally {
            setAssignmentBusy(false);
        }
    };

    return (
        <Layout title="Tracker Detail">
            <input
                id="proof-input"
                type="file"
                hidden
                onChange={handleProofFileChange}
            />
            <Modal
                open={sessionModalOpen}
                onClose={handleSessionModalClose}
            >
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 300, bgcolor: 'background.paper', p: 2,
                    borderRadius: 2, textAlign: 'center', boxShadow: 24
                }}>
                    <Typography variant="h6" color="error" sx={{ mb: 1 }}>
                        Session Expired
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                        Your session has expired. Please log in again.
                    </Typography>
                    <Button variant="contained" onClick={handleSessionModalClose}>
                        Go to Login
                    </Button>
                </Box>
            </Modal>
            {pageBusy && <LinearProgress sx={{ position: 'sticky', top: 0, zIndex: 10 }} />}
            <Container component={Paper} sx={{ mt: 4, p: 3, maxWidth: 600 }}>
                <Typography variant="h5" align="center" gutterBottom>
                    Edit Tracker
                </Typography>
                {trackerLockedForNonAdmin && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {TRACKER_LOCK_UI_MESSAGE}
                    </Alert>
                )}
                {data ? (
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <Card sx={{ mb: 2 }}>
                                <CardHeader title="Current Data" sx={{ bgcolor: '#f5f5f5', p: 1 }} />
                                <CardContent sx={{ p: 2 }}>
                                    {Object.keys(formState)
                                        .filter((key) => !NON_EDITABLE_FIELDS.has(key) && key !== 'proofNotRequired')
                                        .map((key) => (
                                            <Box key={key} mb={1}>
                                                <Typography variant="subtitle2" fontWeight="bold">
                                                    {(key[0].toUpperCase() + key.slice(1)).replace(/([a-z])([A-Z])/g, '$1 $2')}:
                                                </Typography>
                                                <Typography variant="body2" display="inline">
                                                    {key === 'sourceURL' ? (
                                                        <a href={formState[key]} target="_blank" rel="noopener noreferrer">
                                                            {formState[key]}
                                                        </a>
                                                    ) : (
                                                        formState[key]
                                                    )}
                                                    {canEditField(key) && (
                                                        <IconButton onClick={() => handleEditClick(key)} size="small">
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Typography>
                                                {editFields[key] && canEditField(key) && (
                                                    <Box mt={1}>
                                                        {key === 'status' ? (
                                                            <Select
                                                                fullWidth
                                                                name={key}
                                                                value={formState[key]}
                                                                onChange={handleChangeField}
                                                                renderValue={(val) =>
                                                                    STATUS_OPTIONS.find(o => o.value === val)?.label || val
                                                                }
                                                            >
                                                                {STATUS_OPTIONS.map(opt => (
                                                                    <MenuItem value={opt.value} key={opt.value}>
                                                                        <Box
                                                                            component="span"
                                                                            sx={{
                                                                                width: 8,
                                                                                height: 8,
                                                                                borderRadius: '50%',
                                                                                bgcolor: STATUS_COLORS[opt.value],
                                                                                mr: 1,
                                                                                display: 'inline-block',
                                                                                flexShrink: 0
                                                                            }}
                                                                        />
                                                                        {opt.label}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        ) : key.includes('Date') ? (
                                                            <TextField
                                                                fullWidth
                                                                margin="dense"
                                                                name={key}
                                                                type="date"
                                                                value={formState[key]}
                                                                onChange={handleChangeField}
                                                                InputLabelProps={{ shrink: true }}
                                                            />
                                                        ) : (
                                                            <TextField
                                                                fullWidth
                                                                margin="dense"
                                                                name={key}
                                                                value={formState[key]}
                                                                onChange={handleChangeField}
                                                            />
                                                        )}
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            sx={{ mt: 1 }}
                                                            startIcon={<SaveIcon />}
                                                            onClick={() => handleSave(key)}
                                                        >
                                                            Save
                                                        </Button>
                                                    </Box>
                                                )}
                                                <Divider sx={{ mt: 1 }} />
                                            </Box>
                                        ))}
                                    <Box mb={1}>
                                        <Typography variant="subtitle2" fontWeight="bold">Proof not required:</Typography>
                                        {data?.proofNotRequired ? (
                                            <>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked
                                                            onChange={handleProofNotRequiredChange}
                                                            disabled={!isAdmin}
                                                            color="primary"
                                                        />
                                                    }
                                                    label="Yes (only Admin can change)"
                                                />
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    Proof upload is disabled for this tracker. Status was set to Pending for review.
                                                </Typography>
                                            </>
                                        ) : docList.length === 0 ? (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={false}
                                                        onChange={handleProofNotRequiredChange}
                                                        disabled={!isAdmin && trackerLockedForNonAdmin}
                                                        color="primary"
                                                    />
                                                }
                                                label="No — turn on to mark this tracker as Proof not required"
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                To mark as Proof not required, delete all proof documents first.
                                            </Typography>
                                        )}
                                        <Divider sx={{ mt: 1 }} />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        {data && canReassign && (
                            <Grid item xs={12}>
                                <Card sx={{ mb: 2 }}>
                                    <CardHeader
                                        title="People mapped to this tracker"
                                        subheader="Reassign or remove owners and managers. Company owners can only be mapped as tracker Owner; company managers only as tracker Manager. Affected users will be notified."
                                        sx={{ bgcolor: '#f5f5f5', p: 1 }}
                                    />
                                    <CardContent sx={{ p: 2 }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>Owners</Typography>
                                                {ownerList.length === 0 ? (
                                                    <Stack spacing={0.75}>
                                                        <Typography variant="body2" color="text.secondary">No owners assigned</Typography>
                                                        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => handleReassignOpen('Owner')} disabled={assignmentBusy}>
                                                            Assign owners
                                                        </Button>
                                                    </Stack>
                                                ) : (
                                                    <Stack spacing={0.75}>
                                                        {ownerList.map((p) => (
                                                            <Box key={p.email} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={0.5}>
                                                                <Typography variant="body2">{p.name || p.email}</Typography>
                                                                <Stack direction="row" spacing={0.5}>
                                                                    <Tooltip title="Remove from tracker">
                                                                        <IconButton size="small" color="error" onClick={() => handleRemoveClick('Owner', p)} disabled={assignmentBusy}>
                                                                            <PersonRemoveIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </Box>
                                                        ))}
                                                        <Button size="small" startIcon={<SwapHorizIcon />} onClick={() => handleReassignOpen('Owner')} disabled={assignmentBusy}>
                                                            Reassign owners
                                                        </Button>
                                                    </Stack>
                                                )}
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>Managers</Typography>
                                                {managerList.length === 0 ? (
                                                    <Stack spacing={0.75}>
                                                        <Typography variant="body2" color="text.secondary">No managers assigned</Typography>
                                                        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => handleReassignOpen('Manager')} disabled={assignmentBusy}>
                                                            Assign managers
                                                        </Button>
                                                    </Stack>
                                                ) : (
                                                    <Stack spacing={0.75}>
                                                        {managerList.map((p) => (
                                                            <Box key={p.email} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={0.5}>
                                                                <Typography variant="body2">{p.name || p.email}</Typography>
                                                                <Stack direction="row" spacing={0.5}>
                                                                    <Tooltip title="Remove from tracker">
                                                                        <IconButton size="small" color="error" onClick={() => handleRemoveClick('Manager', p)} disabled={assignmentBusy}>
                                                                            <PersonRemoveIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </Box>
                                                        ))}
                                                        <Button size="small" startIcon={<SwapHorizIcon />} onClick={() => handleReassignOpen('Manager')} disabled={assignmentBusy}>
                                                            Reassign managers
                                                        </Button>
                                                    </Stack>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                ) : (
                    <Typography align="center">Loading…</Typography>
                )}
                <Card sx={{ mb: 1 }}>
                    <CardHeader
                        title="Compliance Documents"
                        subheader={proofUploadDisabled ? 'Proof not required' : `${docList.length} file${docList.length !== 1 ? 's' : ''} active`}
                        sx={{ bgcolor: (t) => t.palette.action.hover, py: 1 }}
                    />

                    <CardContent sx={{ p: 1 }}>
                        {proofUploadDisabled ? (
                            <Alert severity="info" icon={false}>
                                This tracker is marked as Proof not required. No proof upload is needed.
                            </Alert>
                        ) : (
                            <>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>File</TableCell>
                                            <TableCell>Uploaded</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {docList.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    No documents
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            docList.map((d) => (
                                                <TableRow key={d.filePath}>
                                                    <TableCell>{d.fileName}</TableCell>
                                                    <TableCell>
                                                        {dateTimeFmt.format(new Date(d.uploadedAt))}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <IconButton size="small" onClick={() => handleOpenPreview(d)}>
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                api
                                                                    .post(
                                                                        's3/get',
                                                                        { trackerId: formState.id, filePath: d.filePath },
                                                                        { responseType: 'blob' }
                                                                    )
                                                                    .then(({ data }) => {
                                                                        const url = URL.createObjectURL(new Blob([data]));
                                                                        const a = document.createElement('a');
                                                                        a.href = url;
                                                                        a.download = d.fileName;
                                                                        a.click();
                                                                        URL.revokeObjectURL(url);
                                                                    })
                                                            }
                                                            disabled={docDeletingId === d.filePath}
                                                        >
                                                            {docDeletingId === d.filePath ? (
                                                                <CircularProgress size={16} />
                                                            ) : (
                                                                <DownloadIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                        {canManageProofDocs && (
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleDeleteFile(d)}
                                                                disabled={docDeletingId === d.filePath}
                                                            >
                                                                {docDeletingId === d.filePath ? (
                                                                    <CircularProgress size={16} />
                                                                ) : (
                                                                    <DeleteIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>

                                {canManageProofDocs && (
                                    <>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>
                                            Upload Proof (max 5 active)
                                        </Typography>

                                        <FormControl fullWidth sx={{ mt: 1 }}>
                                            <Input
                                                type="file"
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <UploadIcon />
                                                    </InputAdornment>
                                                }
                                                onChange={(e) => {
                                                    setUploadedFile(e.target.files[0]);
                                                    setIsSubmitDisabled(false);
                                                }}
                                            />
                                        </FormControl>

                                        <LoadingButton
                                            variant="contained"
                                            sx={{ mt: 1 }}
                                            disabled={!uploadedFile || docList.length >= 5}
                                            onClick={handleSubmitFile}
                                            loading={fileBusy}
                                            loadingPosition="start"
                                            startIcon={<UploadIcon />}
                                        >
                                            {docList.length ? 'Add New File' : 'Submit'}
                                        </LoadingButton>
                                    </>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>

                <Modal open={previewModalOpen} onClose={handlePreviewClose}>
                    <Box sx={{
                        position: 'absolute', top: '50%', left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '80vw', height: '100%',
                        bgcolor: 'background.paper', p: 2, borderRadius: 2, boxShadow: 24, display: 'flex', flexDirection: 'column'
                    }}>
                        <Typography variant="h6">File preview</Typography>
                        {previewUrl && (
                            <iframe src={previewUrl} title="preview" style={{ border: 0, flex: 1 }} />
                        )}
                        <Box sx={{ mt: 2, textAlign: 'right' }}>
                            <Button variant="contained" onClick={handlePreviewClose}>Close</Button>
                        </Box>
                    </Box>
                </Modal>
                <Accordion sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">Comments History</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Button
                            variant="contained" size="small" sx={{ mb: 1 }}
                            startIcon={<AddIcon />} onClick={() => setOpenCommentModal(true)}
                            disabled={!canMutateTracker}
                        >
                            Add
                        </Button>
                        {comments.length === 0 ? (
                            <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                                No comments yet. Be the first to leave one!
                            </Alert>
                        ) : (
                            <Box>
                                {comments.map((c) => (
                                    <Box key={c._id} mb={1}>
                                        <Box display="flex" justifyContent="space-between">
                                            <Box>
                                                <Typography variant="body2">{c.text}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {c.postedBy?.name || c.postedBy} • {dateTimeFmt.format(new Date(c.created))}
                                                </Typography>
                                            </Box>
                                            {c.postedBy?.id === currentUserId && canMutateTracker && (
                                                <IconButton size="small" color="error" onClick={() => handleDeleteComment(c)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                        <Divider sx={{ mt: .5 }} />
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </AccordionDetails>
                </Accordion>
                <Accordion sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">Sub Tasks</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Button
                            variant="contained"
                            size="small"
                            sx={{ mb: 1 }}
                            startIcon={<AddIcon />}
                            onClick={() => {
                                setEditingSubTask(null);
                                setOpenSubTaskModal(true);
                            }}
                            disabled={!canMutateTracker}
                        >
                            Add
                        </Button>
                        <Card sx={{ mt: 2, borderRadius: 3, boxShadow: 4, overflow: 'hidden' }}>
                            <CardHeader
                                title="Sub-Tasks"
                                subheader={`${filteredSubTasks.length} item${filteredSubTasks.length !== 1 ? 's' : ''}${subTasks.length !== filteredSubTasks.length ? ` • filtered from ${subTasks.length}` : ''}`}
                                sx={{
                                    bgcolor: (t) => t.palette.mode === 'light' ? 'primary.main' : 'primary.dark',
                                    color: 'primary.contrastText',
                                    py: 1.2
                                }}
                                action={
                                    <Tooltip title="Filters">
                                        <FilterListIcon />
                                    </Tooltip>
                                }
                            />
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'background.default'
                                }}
                            >
                                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                                    <TextField
                                        size="small"
                                        placeholder="Search sub-tasks…"
                                        value={subtaskQuery}
                                        onChange={(e) => startFiltersTransition(() => setSubtaskQuery(e.target.value))}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ minWidth: 260 }}
                                    />
                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                            Risk:
                                        </Typography>
                                        {['Low', 'Medium', 'High'].map(r => {
                                            const active = subtaskRiskFilter.includes(r);
                                            return (
                                                <Chip
                                                    key={r}
                                                    size="small"
                                                    onClick={() =>
                                                        startFiltersTransition(() =>
                                                            setSubtaskRiskFilter(a => active ? a.filter(x => x !== r) : [...a, r])
                                                        )
                                                    }
                                                    label={r}
                                                    variant={active ? 'filled' : 'outlined'}
                                                    color={r === 'High' ? 'error' : r === 'Medium' ? 'warning' : 'success'}
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            );
                                        })}
                                    </Stack>
                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                            Status:
                                        </Typography>
                                        {['Pending', 'In-Progress', 'Complied'].map(s => {
                                            const active = subtaskStatusFilter.includes(s);
                                            return (
                                                <Chip
                                                    key={s}
                                                    size="small"
                                                    onClick={() =>
                                                        startFiltersTransition(() =>
                                                            setSubtaskStatusFilter(a => active ? a.filter(x => x !== s) : [...a, s])
                                                        )
                                                    }
                                                    label={s.replace('-', ' ')}
                                                    variant={active ? 'filled' : 'outlined'}
                                                    color={s === 'Complied' ? 'success' : s === 'In-Progress' ? 'info' : 'warning'}
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            );
                                        })}
                                    </Stack>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                            Sort:
                                        </Typography>
                                        {[
                                            { k: 'complianceActivity', label: 'Activity' },
                                            { k: 'risk', label: 'Risk' },
                                            { k: 'status', label: 'Status' },
                                        ].map(opt => (
                                            <Chip
                                                key={opt.k}
                                                size="small"
                                                onClick={() =>
                                                    startFiltersTransition(() =>
                                                        setSubtaskSort(s => s.by === opt.k ? ({ ...s, dir: s.dir === 'asc' ? 'desc' : 'asc' }) : ({ by: opt.k, dir: 'asc' }))
                                                    )
                                                }
                                                label={`${opt.label}${subtaskSort.by === opt.k ? (subtaskSort.dir === 'asc' ? ' ↑' : ' ↓') : ''}`}
                                                variant={subtaskSort.by === opt.k ? 'filled' : 'outlined'}
                                            />
                                        ))}
                                    </Stack>
                                </Stack>
                            </Box>
                            {filtersTransitionPending && <LinearProgress sx={{ borderRadius: 0 }} />}
                            <TableContainer sx={{ maxHeight: 420 }}>
                                <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
                                    <TableHead>
                                        <TableRow>
                                            {['Compliance Activity', 'Description', 'Owners', 'Managers', 'Risk', 'Status', 'Created By', 'Actions'].map(h => (
                                                <TableCell
                                                    key={h}
                                                    sx={{
                                                        bgcolor: 'primary.main',
                                                        color: 'common.white',
                                                        fontWeight: 700,
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {h}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredSubTasks.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={8} sx={{ py: 5 }}>
                                                    <Alert severity="info" icon={false} sx={{ borderRadius: 2, textAlign: 'center' }}>
                                                        No sub-tasks match your filters.
                                                    </Alert>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filteredSubTasks
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((t) => (
                                                    <TableRow key={t._id} hover>
                                                        <TableCell
                                                            sx={(theme) => ({
                                                                fontWeight: 600,
                                                                position: 'relative',
                                                                pl: 2.5,
                                                                '&:before': {
                                                                    content: '""',
                                                                    position: 'absolute',
                                                                    left: 0,
                                                                    top: 8,
                                                                    bottom: 8,
                                                                    width: 4,
                                                                    borderRadius: 2,
                                                                    backgroundColor: riskColor(theme, t.risk),
                                                                },
                                                            })}
                                                        >
                                                            {t.complianceActivity}
                                                        </TableCell>
                                                        <TableCell sx={{ maxWidth: 280 }}>
                                                            <Tooltip title={t.description || ''}>
                                                                <Typography variant="body2" color="text.secondary" noWrap>
                                                                    {t.description}
                                                                </Typography>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell>
                                                            <EmailAvatars csv={t.owners || ''} />
                                                        </TableCell>
                                                        <TableCell>
                                                            <EmailAvatars csv={t.managers || ''} />
                                                        </TableCell>
                                                        <TableCell>
                                                            <RiskChipUI value={t.risk} />
                                                        </TableCell>
                                                        <TableCell>
                                                            <StatusChipUI value={t.status} />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">{t.postedBy?.name || '—'}</Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Stack direction="row" spacing={3} justifyContent="flex-end" alignItems="center">
                                                                <Tooltip title="Proofs">
                                                                    <Badge badgeContent={t.signedDocs?.length ?? 0} color="primary" showZero>
                                                                        <UploadIcon fontSize="small" />
                                                                    </Badge>
                                                                </Tooltip>
                                                                <Tooltip title="Comments">
                                                                    <Badge badgeContent={t.comments?.length ?? 0} color="secondary" showZero>
                                                                        <SendIcon fontSize="small" />
                                                                    </Badge>
                                                                </Tooltip>
                                                                <Button size="small" variant="contained" onClick={() => openDetail(t)} disabled={subtaskLoadingId === t._id}>
                                                                    {subtaskLoadingId === t._id ? <CircularProgress size={16} /> : 'View'}
                                                                </Button>
                                                                {(t.postedBy?.id === currentUserId) && canMutateTracker && (
                                                                    <>
                                                                        <Tooltip title="Edit Sub-Task">
                                                                            <IconButton onClick={() => openEdit(t)} size="small">
                                                                                <EditIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Tooltip title="Delete Sub-Task">
                                                                            <IconButton onClick={() => handleDeleteSubTask(t._id)} size="small" color="error">
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </>
                                                                )}
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Showing {Math.min(rowsPerPage, filteredSubTasks.length - page * rowsPerPage)} of {filteredSubTasks.length}
                                </Typography>
                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25]}
                                    component="div"
                                    count={filteredSubTasks.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    showFirstButton
                                    showLastButton
                                />
                            </CardActions>
                        </Card>
                        {Boolean(subtaskLoadingId) && (
                            <LinearProgress sx={{ borderRadius: 0 }} />
                        )}
                    </AccordionDetails>
                </Accordion>
            </Container>
            <Drawer
                anchor="right"
                open={detailOpen}
                onClose={closeDetail}
                PaperProps={{ sx: { width: { xs: '100%', sm: 520, md: 560 } } }}
            >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ flex: 1 }} noWrap>
                        {selectedSubTask?.complianceActivity || 'Sub-Task'}
                    </Typography>
                    {selectedSubTask?.status && (
                        <Chip
                            size="small"
                            color={selectedSubTask.status === 'Complied' ? 'success' :
                                selectedSubTask.status === 'In-Progress' ? 'info' : 'warning'}
                            label={String(selectedSubTask.status).replace('-', ' ')}
                            variant="filled"
                        />
                    )}
                    {selectedSubTask?.risk && (
                        <Chip
                            size="small"
                            color={selectedSubTask.risk === 'High' ? 'error' :
                                selectedSubTask.risk === 'Medium' ? 'warning' : 'success'}
                            label={selectedSubTask.risk}
                            variant="outlined"
                        />
                    )}
                </Box>
                <Tabs
                    value={detailTab}
                    onChange={(_, v) => setDetailTab(v)}
                    variant="fullWidth"
                >
                    <Tab label="Overview" />
                    <Tab label={`Proofs${selectedSubTask?.signedDocs ? ` (${selectedSubTask.signedDocs.length})` : ''}`} />
                    <Tab label={`Comments${selectedSubTask?.comments ? ` (${selectedSubTask.comments.length})` : ''}`} />
                </Tabs>
                {subtaskLoadingId === selectedSubTask?._id && <LinearProgress sx={{ position: 'sticky', top: 48, zIndex: 2 }} />}
                <Box sx={{ p: 2, overflow: 'auto' }}>
                    {detailTab === 0 && (
                        <Stack spacing={1.5}>
                            <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                            <Typography variant="body2">{selectedSubTask?.description || '—'}</Typography>
                            <Divider />
                            <Grid container spacing={1}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Owners</Typography>
                                    {(selectedSubTask?.owners || '')
                                        .split(',').filter(Boolean).map(e => (
                                            <Chip key={e} size="small" label={e} sx={{ mr: .5, mb: .5 }} />
                                        ))}
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Managers</Typography>
                                    {(selectedSubTask?.managers || '')
                                        .split(',').filter(Boolean).map(e => (
                                            <Chip key={e} size="small" label={e} sx={{ mr: .5, mb: .5 }} />
                                        ))}
                                </Grid>
                            </Grid>
                            <Grid container spacing={1}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Consequence</Typography>
                                    <Typography variant="body2">{selectedSubTask?.consequence || '—'}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Completion Date</Typography>
                                    <Typography variant="body2">
                                        {selectedSubTask?.completionDate ? dateFmt.format(new Date(selectedSubTask.completionDate)) : '—'}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => openProofUpload(selectedSubTask?._id)}
                                    startIcon={<UploadIcon />}
                                    disabled={proofBusy || proofUploadDisabled || !canMutateTracker}
                                >
                                    Upload Proof
                                </Button>
                                {(selectedSubTask?.postedBy?.id === currentUserId) && canMutateTracker && (
                                    <Button
                                        variant="contained"
                                        onClick={() => openEdit(selectedSubTask)}
                                        startIcon={<EditIcon />}
                                    >
                                        Edit
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                    )}
                    {detailTab === 1 && (
                        <Stack spacing={1.5}>
                            {!selectedSubTask?.signedDocs && (
                                <Stack spacing={1}>
                                    {[...Array(3)].map((_, i) => <Skeleton key={i} height={36} />)}
                                </Stack>
                            )}
                            {selectedSubTask?.signedDocs?.length ? (
                                <Box>
                                    {selectedSubTask.signedDocs.map((d) => (
                                        <Chip
                                            key={d.url}
                                            label={d.fileName}
                                            component="a"
                                            href={d.url}
                                            target="_blank"
                                            clickable
                                            sx={{ mr: 1, mb: 1 }}
                                        />
                                    ))}
                                </Box>
                            ) : selectedSubTask?.signedDocs ? (
                                <Alert severity="info" icon={false}>No proofs uploaded yet.</Alert>
                            ) : null}
                            <Divider />
                            <Button
                                variant="outlined"
                                onClick={() => openProofUpload(selectedSubTask?._id)}
                                startIcon={<UploadIcon />}
                                sx={{ alignSelf: 'flex-start' }}
                                disabled={proofBusy || proofUploadDisabled || !canMutateTracker}
                            >
                                Upload Proof
                            </Button>
                        </Stack>
                    )}
                    {detailTab === 2 && (
                        <Stack spacing={1.5}>
                            <Box
                                sx={{
                                    maxHeight: 260,
                                    overflowY: 'auto',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 1
                                }}
                            >
                                {!selectedSubTask?.comments && (
                                    <Stack spacing={1}>{[...Array(4)].map((_, i) => <Skeleton key={i} height={28} />)}</Stack>
                                )}
                                {selectedSubTask?.comments?.length ? (
                                    <List dense>
                                        {selectedSubTask.comments.map((c) => (
                                            <ListItem key={c._id || c.createdAt} disableGutters>
                                                <ListItemText
                                                    primary={c.text}
                                                    secondary={`${c.postedBy?.name || ''} · ${dateTimeFmt.format(new Date(c.createdAt))}`}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                ) : selectedSubTask?.comments ? (
                                    <Alert severity="info" icon={false}>No comments yet.</Alert>
                                ) : null}
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Add a comment"
                                    value={commentText}
                                    onChange={(e) => setCommentText(e.target.value)}
                                    disabled={!canMutateTracker}
                                />
                                <IconButton
                                    color="primary"
                                    disabled={!commentText.trim() || commentBusy || !canMutateTracker}
                                    onClick={() => handleAddComment(selectedSubTask._id)}
                                >
                                    {commentBusy ? <CircularProgress size={18} /> : <SendIcon />}
                                </IconButton>
                            </Box>
                        </Stack>
                    )}
                </Box>
            </Drawer>
            <Dialog open={openCommentModal} onClose={() => setOpenCommentModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add a Comment</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Comment" fullWidth multiline rows={3} sx={{ mt: 1 }}
                        value={newComment} onChange={(e) => setNewComment(e.target.value)}
                        disabled={!canMutateTracker}
                    />
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setOpenCommentModal(false)}>Cancel</Button>
                    <LoadingButton variant="contained" onClick={handleSubmitComment} loading={commentBusy} startIcon={<SendIcon />} loadingPosition="start">
                        Add Comment
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            <Dialog
                open={openSubTaskModal}
                onClose={() => setOpenSubTaskModal(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle
                    sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        py: 1.5,
                        pl: 3
                    }}
                >
                    {editingSubTask ? 'Edit Sub-Task' : 'New Sub-Task'}
                </DialogTitle>
                <DialogContent dividers sx={{ pt: 3, pb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Compliance Activity"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={newSubTask.complianceActivity}
                                onChange={(e) =>
                                    setNewSubTask({ ...newSubTask, complianceActivity: e.target.value })
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Description"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={newSubTask.description}
                                onChange={(e) =>
                                    setNewSubTask({ ...newSubTask, description: e.target.value })
                                }
                                disabled={Boolean(editingSubTask)}
                                helperText={editingSubTask ? "Description can't be changed after creation" : ""}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="owners-label">Owners</InputLabel>
                                <Select
                                    labelId="owners-label"
                                    multiple
                                    value={newSubTask.owners}
                                    label="Owners"
                                    renderValue={(sel) => sel.join(', ')}
                                    onChange={(e) =>
                                        setNewSubTask({ ...newSubTask, owners: e.target.value })
                                    }
                                >
                                    {owners.map((o) => (
                                        <MenuItem key={o._id} value={o.email}>
                                            {o.email}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="managers-label">Managers</InputLabel>
                                <Select
                                    labelId="managers-label"
                                    multiple
                                    value={newSubTask.managers}
                                    label="Managers"
                                    renderValue={(sel) => sel.join(', ')}
                                    onChange={(e) =>
                                        setNewSubTask({ ...newSubTask, managers: e.target.value })
                                    }
                                >
                                    {managers.map((m) => (
                                        <MenuItem key={m._id} value={m.email}>
                                            {m.email}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="risk-label">Risk</InputLabel>
                                <Select
                                    labelId="risk-label"
                                    value={newSubTask.risk}
                                    label="Risk"
                                    onChange={(e) =>
                                        setNewSubTask({ ...newSubTask, risk: e.target.value })
                                    }
                                >
                                    {['Low', 'Medium', 'High'].map((r) => (
                                        <MenuItem key={r} value={r}>
                                            {r}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Consequence"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={newSubTask.consequence}
                                onChange={(e) =>
                                    setNewSubTask({ ...newSubTask, consequence: e.target.value })
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="status-label">Status</InputLabel>
                                <Select
                                    labelId="status-label"
                                    value={newSubTask.status}
                                    label="Status"
                                    onChange={(e) =>
                                        setNewSubTask({ ...newSubTask, status: e.target.value })
                                    }
                                >
                                    {['Pending', 'In-Progress', 'Complied'].map((s) => (
                                        <MenuItem key={s} value={s}>
                                            {s.replace('-', ' ')}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                type="date"
                                label="Completion Date"
                                variant="outlined"
                                size="small"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={newSubTask.completionDate}
                                onChange={(e) =>
                                    setNewSubTask({ ...newSubTask, completionDate: e.target.value })
                                }
                            />
                        </Grid>
                    </Grid>
                    {editingSubTask && (
                        <>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="subtitle2" gutterBottom>
                                Proofs
                            </Typography>
                            {editingSubTask.signedDocs?.length ? (
                                <Box sx={{ mb: 2 }}>
                                    {editingSubTask.signedDocs.map((d) => (
                                        <Chip
                                            key={d.url}
                                            label={d.fileName}
                                            component="a"
                                            href={d.url}
                                            target="_blank"
                                            clickable
                                            sx={{ mr: 1, mb: 1 }}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    No proofs uploaded
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                                {proofUploadDisabled ? (
                                    <Typography variant="body2" color="text.secondary">Proof upload is disabled for this tracker (Proof not required).</Typography>
                                ) : (
                                    <>
                                        <Button component="label" variant="outlined" sx={{ flexShrink: 0 }} disabled={!canMutateTracker}>
                                            Browse…
                                            <input hidden type="file" onChange={(e) => setProofFile(e.target.files[0])} />
                                        </Button>
                                        <LoadingButton
                                            variant="contained"
                                            disabled={!proofFile || !canMutateTracker}
                                            onClick={handleProofUpload}
                                            loading={proofBusy}
                                            loadingPosition="start"
                                            startIcon={<UploadIcon />}
                                        >
                                            Upload
                                        </LoadingButton>
                                    </>
                                )}
                            </Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Comments
                            </Typography>
                            <Box
                                sx={{
                                    maxHeight: 160,
                                    overflowY: 'auto',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 1,
                                    mb: 2
                                }}
                            >
                                {editingSubTask.comments?.length ? (
                                    <List dense>
                                        {editingSubTask.comments.map((c) => (
                                            <ListItem key={c._id || c.createdAt} disableGutters>
                                                <ListItemText
                                                    primary={c.text}
                                                    secondary={`${c.postedBy.name} · ${dateTimeFmt.format(new Date(c.createdAt))}`}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No comments yet
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Add a comment"
                                    value={commentText}
                                    onChange={(e) => setCommentText(e.target.value)}
                                    disabled={!canMutateTracker}
                                />
                                <IconButton
                                    color="primary"
                                    disabled={!commentText.trim() || commentBusy || !canMutateTracker}
                                    onClick={() => {
                                        handleAddComment(editingSubTask._id);
                                    }}
                                >
                                    {commentBusy ? <CircularProgress size={18} /> : <SendIcon />}
                                </IconButton>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.5 }}>
                    <Button
                        onClick={() => setOpenSubTaskModal(false)}
                        variant="outlined"
                        color="secondary"
                        disabled={subtaskloading}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={handleSubmitSubTask}
                        variant="contained"
                        loading={subtaskloading}
                        loadingPosition="start"
                        startIcon={<SaveIcon />}
                        disabled={!canMutateTracker}
                    >
                        {editingSubTask ? 'Save Changes' : 'Add Sub-Task'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            {/* Reassign dialog */}
            <Dialog open={reassignDialogOpen} onClose={handleReassignClose} maxWidth="sm" fullWidth>
                <DialogTitle>Reassign {reassignRole ? reassignRole + 's' : ''}</DialogTitle>
                <DialogContent dividers sx={{ pt: 2 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="reassign-select-label">Select {(reassignRole ?? '').toLowerCase() + 's'}</InputLabel>
                        <Select
                            labelId="reassign-select-label"
                            multiple
                            value={reassignSelected}
                            label={`Select ${(reassignRole ?? '').toLowerCase() + 's'}`}
                            renderValue={(sel) => sel.join(', ')}
                            onChange={(e) => setReassignSelected(e.target.value)}
                        >
                            {assignableUsersForRole.map((u) => (
                                <MenuItem key={u.email} value={u.email}>
                                    {u.name || u.email}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Keep people selected to leave them on the tracker; add or remove to change. Only added or removed users are notified. At least one owner or one manager must remain.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleReassignClose} variant="outlined">Cancel</Button>
                    <LoadingButton
                        variant="contained"
                        onClick={handleReassignSubmit}
                        loading={assignmentBusy}
                        loadingPosition="start"
                        startIcon={<SwapHorizIcon />}
                        disabled={reassignSelected.length === 0}
                    >
                        Update assignment
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            {/* Remove person confirmation */}
            <Dialog open={removeDialogOpen} onClose={handleRemoveClose}>
                <DialogTitle>Remove from tracker</DialogTitle>
                <DialogContent>
                    <Typography>
                        Remove <strong>{removeTarget?.name || removeTarget?.email}</strong> as {removeRole?.toLowerCase()}? They will be notified.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRemoveClose}>Cancel</Button>
                    <LoadingButton variant="contained" color="error" onClick={handleRemoveConfirm} loading={assignmentBusy} startIcon={<PersonRemoveIcon />}>
                        Remove
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Layout>
    );
};

export default EditPage;

